import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { IPage } from '../../../models/page';
import { useAppDispatch, useAppSelector } from '../../../store';
import { TableActions } from '../../../store/entities/table/table.actions';
import { VouchersActions } from '../../../store/entities/vouchers/vouchers.actions';
import usePermissionRedirect from '../../../hooks/usePermissionRedirect';
import useSiteOptions from '../../../hooks/useSiteOptions';
import useVoucherDynamicOptions from '../../../hooks/useVoucherDynamicOptions';
import { FieldTypes, IFormFields } from '../../../models/form';
import useUrlSearchParam from '../../../hooks/useUrlSearchParams';
import useTranslations from '../../../hooks/useTranslations';
import Title from '../../../components/UI/Title/Title';
import Filters from '../../../components/Container/Filters/Filters';
import EnhancedTable from '../../../components/UI/RcTable/EnhancedTable';
import { CONSTANTS } from '../../../utils/constants';
import {
  getDateTimeAndSeconds,
  sortDataByDate,
} from '../../../utils/formatters';
import { IAutoCompleteItem } from '../../../models/autoComplete';
import TableButtons from '../../../components/UI/TableButtons/TableButtons';
import { IButton } from '../../../models/button';
import { PERMISSIONS } from '../../../utils/permissions';
import ActionButtons from '../../../components/UI/ActionButtons/ActionButtons';
import { vouchersListSearchValidation } from '../../../utils/formValidation/vouchers';
import { Voucher } from '../../../store/entities/vouchers/type';
import { VoucherStatus } from '../../../models/vouchers';

const ManageVouchers = ({ permission, nextRoute }: IPage) => {
  const [rows, setRows] = useState<any[]>([]);
  const siteList = useSiteOptions();
  const { types: typeList, statuses: statusList } =
    useVoucherDynamicOptions(true);

  const dispatch = useAppDispatch();
  const { rcTranslate } = useTranslations();
  const { getValueFromQuery, params } = useUrlSearchParam();
  const navigate = useNavigate();

  const { totalRows, nextPageNumber, currentPage, sortModel, queryParams } =
    useAppSelector((state) => state.table);
  const { vouchers } = useAppSelector((state) => state.vouchers);
  usePermissionRedirect(permission, nextRoute);
  const { appPermissions }: { appPermissions: string[] } = useAppSelector(
    (store) => store.auth
  );

  useEffect(() => {
    if (!permission) return;
    dispatch(TableActions.setTableData({ currentPage: 1 }));
    dispatch(VouchersActions.getVouchers(params));
  }, [dispatch, params, permission]);

  useEffect(() => {
    if (nextPageNumber) {
      const getData = async () => {
        await dispatch(
          VouchersActions.getVouchers(
            undefined,
            sortModel,
            false,
            nextPageNumber
          )
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [dispatch, nextPageNumber, sortModel]);

  useEffect(() => {
    if (sortModel.length) {
      const getData = async () => {
        await dispatch(
          VouchersActions.getVouchers(undefined, sortModel, false)
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [dispatch, sortModel]);

  const columns: any[] = useMemo(
    () => [
      {
        id: 'status',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('vouchers.filters.status'),
      },
      {
        id: 'type',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('vouchers.filters.type'),
      },
      {
        id: 'siteId',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('vouchers.filters.siteId'),
      },
      {
        id: 'customerPhoneNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('vouchers.filters.customerPhoneNumber'),
      },
      {
        id: 'created',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('users.filters.registrationDate'),
      },
      {
        id: 'expiryDate',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('vouchers.vouchersDetails.fieldNames.expiryDate'),
      },
      {
        id: 'durationInMonths',
        numeric: true,
        disablePadding: true,
        label: rcTranslate(
          'vouchers.vouchersDetails.fieldNames.durationInMonths'
        ),
      },
      {
        id: 'orderId',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('vouchers.vouchersDetails.fieldNames.orderId'),
      },
    ],
    [rcTranslate]
  );

  const mapVouchersToTableRows = useCallback(
    (list: Voucher[]) => {
      const getLabel = (items: IAutoCompleteItem[], id?: number | null) =>
        items.find((i) => i.id === id)?.label ?? id ?? '';
      return list.map((v) => ({
        id: v.id,
        status: getLabel(statusList, v.status),
        type: getLabel(typeList, v.type),
        siteId: getLabel(siteList, v.siteId),
        customerPhoneNumber: v.customerPhoneNumber ?? '',
        created: v.created ? getDateTimeAndSeconds(v.created) : '',
        expiryDate: v.expiryDate ? getDateTimeAndSeconds(v.expiryDate) : '',
        durationInMonths: v.durationInMonths ?? null,
        orderId: v.orderId ?? '',
        actions: (
          <ActionButtons
            style={{ marginRight: 'auto' }}
            hide={
              [PERMISSIONS.UPDATE_VOUCHER, PERMISSIONS.REMOVE_VOUCHER].filter(
                (v: string) => appPermissions.includes(v)
              ).length === 0 ||
              ![VoucherStatus.ACTIVE, VoucherStatus.EXPIRED].includes(v.status)
            }
            menuItems={[
              {
                label:
                  'vouchers.manageVouchers.rowActions.update_voucher_details',
                onClick: () =>
                  navigate(
                    `/${CONSTANTS.ROUTES.VOUCHERS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.VOUCHERS_MANAGEMENT.CHILDREN.VOUCHER_DETAILS}/${v.id}`
                  ),
                show:
                  appPermissions.includes(PERMISSIONS.UPDATE_VOUCHER) &&
                  [VoucherStatus.ACTIVE, VoucherStatus.EXPIRED].includes(
                    v.status
                  ),
              },
              {
                label: 'vouchers.manageVouchers.rowActions.cancel_voucher',
                onClick: async () => {
                  const res = await dispatch(
                    VouchersActions.removeVoucher(v.id)
                  );
                  dispatch(
                    VouchersActions.getVouchers(params, undefined, true)
                  );
                },
                show:
                  appPermissions.includes(PERMISSIONS.REMOVE_VOUCHER) &&
                  v.status === VoucherStatus.ACTIVE,
              },
            ]}
          />
        ),
      }));
    },
    [statusList, typeList, siteList, appPermissions, navigate, dispatch, params]
  );

  useEffect(() => {
    if (!vouchers?.length) return setRows([]);
    setRows(mapVouchersToTableRows(vouchers));
  }, [vouchers, mapVouchersToTableRows]);

  const searchFields: IFormFields = {
    initialValues: {
      status: getValueFromQuery('status', 'number'),
      type: getValueFromQuery('type', 'number'),
      siteId: getValueFromQuery('siteId', 'number'),
      customerPhoneNumber: getValueFromQuery('customerPhoneNumber'),
      fromDate: getValueFromQuery('fromDate'),
      toDate: getValueFromQuery('toDate'),
      orderId: getValueFromQuery('orderId'),
    },
    formData: [
      {
        type: FieldTypes.CHILDREN,
        id: 'externalFieldsWrapper',
        fields: [
          {
            type: FieldTypes.AUTOCOMPLETE,
            id: 'status',
            label: 'vouchers.filters.status',
            items: statusList,
            props: { multiple: false },
          },
          {
            type: FieldTypes.AUTOCOMPLETE,
            id: 'type',
            label: 'vouchers.filters.type',
            items: typeList,
            props: { multiple: false },
          },
          {
            type: FieldTypes.AUTOCOMPLETE,
            id: 'siteId',
            label: 'vouchers.filters.siteId',
            items: siteList,
            props: { multiple: false },
          },
        ],
      },
      {
        type: FieldTypes.CHILDREN,
        id: 'extendedFieldsWrapper',
        fields: [
          {
            type: FieldTypes.CHILDREN,
            id: 'extendedFieldsContainer',
            fields: [
              {
                type: FieldTypes.CHILDREN,
                id: 'extendedFields',
                className: 'filters__extendedFields',
                fields: [
                  {
                    type: FieldTypes.NUMBER,
                    id: 'orderId',
                    label: 'vouchers.filters.orderId',
                  },
                  {
                    type: FieldTypes.TEXT,
                    id: 'customerPhoneNumber',
                    label: 'vouchers.filters.customerPhoneNumber',
                  },
                  {
                    type: FieldTypes.DATE,
                    id: 'fromDate',
                    label: 'vouchers.filters.fromDate',
                    props: { getStartOfDay: true },
                  },
                  {
                    type: FieldTypes.DATE,
                    id: 'toDate',
                    label: 'vouchers.filters.toDate',
                    props: {
                      getFullDayTime: true,
                      numOfDays: 1,
                    },
                  },
                ],
              },
              {
                type: FieldTypes.CHILDREN,
                id: 'actionsContainer',
                className: 'filters__extendedActions',
                fields: [
                  {
                    type: FieldTypes.BUTTON,
                    id: 'closeExtendedSearch',
                    label: 'searchFields.closeExtendedSearch',
                  },
                  {
                    type: FieldTypes.CHILDREN,
                    id: 'submitButtonWrapper',
                    className: 'filters__submitButtonWrapper',
                    fields: [
                      {
                        type: FieldTypes.SUBMIT_BUTTON,
                        id: 'submitButton',
                        label: '',
                      },
                      {
                        type: FieldTypes.BUTTON,
                        id: 'cleanSearch',
                        label: 'searchFields.cleanSearch',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  const tableButtons: IButton[] = [
    {
      title: 'vouchers.manageVouchers.addVouchers',
      onClick: () => {
        navigate(
          '/' +
            CONSTANTS.ROUTES.VOUCHERS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.VOUCHERS_MANAGEMENT.CHILDREN.VOUCHER_DETAILS
        );
      },
      show: appPermissions.includes(PERMISSIONS.ADD_VOUCHER),
    },
  ];

  return (
    <>
      <Title text="vouchers.manageVouchers.pageTitle" />

      <Filters
        fields={searchFields}
        filtersValidation={vouchersListSearchValidation}
      />

      <TableButtons generalButtons={[]} tableButtons={tableButtons} />

      <div className="vouchersList">
        <EnhancedTable
          rows={rows}
          columns={columns}
          sortDataByDate={sortDataByDate}
          checkboxSelection={false}
          expandable={false}
          showId={false}
        />
      </div>
    </>
  );
};

export default ManageVouchers;
